import { useEffect, useState, type CSSProperties, type ReactElement } from 'react';
import { useI18n } from '../i18n';
import {
  fetchQuotaUsageResult,
  setQuota,
  useQuota,
  type QuotaUsageFetchResult,
} from '../state/quota';
import { notifyActionError } from '../state/toasts';

/**
 * Phase C-3 Task 9 — Usage section in SettingsDialog.
 *
 * Read-only consumer of useQuota(). Pulled out of SettingsDialog (already
 * ~1322 lines) so it can be tested in isolation and so the eventual
 * SettingsDialog refactor (CLAUDE.md C-2 follow-up list) has one less
 * piece glued in by hand.
 *
 * Render contract:
 * - `null` snapshot (pre-bootstrap, unauth, or fetcher returned null) →
 *   the whole section is hidden. The dialog still renders fine without
 *   it; nothing else in the dialog depends on a visible Usage block.
 * - `limit === -1` (unlimited / dev backstop) → show the heading and an
 *   "Unlimited" line. We intentionally do NOT hide here (unlike the
 *   header badge), because the user opening Settings explicitly asked
 *   "what's my usage?" and seeing nothing would feel broken.
 * - finite `limit` → hosted/system-key usage only. BYOK proxy rows are
 *   intentionally excluded by the daemon, because BYOK has no run limit.
 * - `override !== null` (and limit isn't -1) → also show a small note
 *   that a custom override is in effect, so an admin-suspended user
 *   isn't left wondering why their limit looks weird.
 */
export function UsageSection(): ReactElement | null {
  const q = useQuota();
  const { t } = useI18n();
  const [loadError, setLoadError] = useState<string | null>(null);
  useEffect(() => {
    if (q) return;
    let cancelled = false;
    void fetchQuotaUsageResult().then((result) => {
      if (cancelled) return;
      if (result.ok) {
        setLoadError(null);
        setQuota(result.usage);
        return;
      }
      setQuota(null);
      setLoadError(formatUsageLoadFailure(result));
      if (result.status === 401 || result.status === 403) {
        notifyActionError("Couldn't load usage", {
          status: result.status,
          message: result.message,
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [q]);

  if (!q) {
    if (!loadError) return null;
    return (
      <section
        className="settings-section"
        data-testid="settings-quota-section"
      >
        <div className="section-head">
          <div>
            <h3>{t('quota.sectionTitle')}</h3>
          </div>
        </div>
        <p role="alert" style={usageLoadErrorStyle}>
          {loadError}
        </p>
      </section>
    );
  }

  const isUnlimited = q.limit === -1;
  const showOverride = q.override !== null && !isUnlimited;
  const resetDate =
    q.resetsAt > 0 ? new Date(q.resetsAt).toLocaleDateString() : '';

  return (
    <section
      className="settings-section"
      data-testid="settings-quota-section"
    >
      <div className="section-head">
        <div>
          <h3>{t('quota.sectionTitle')}</h3>
        </div>
      </div>
      {isUnlimited ? (
        <p>{t('quota.unlimited')}</p>
      ) : (
        <>
          <p>{t('quota.usedOfLimit', { used: q.used, limit: q.limit })}</p>
          {resetDate ? (
            <p>{t('quota.resetsOn', { date: resetDate })}</p>
          ) : null}
        </>
      )}
      {showOverride ? <p>{t('quota.override')}</p> : null}
    </section>
  );
}

function formatUsageLoadFailure(
  failure: Extract<QuotaUsageFetchResult, { ok: false }>,
): string {
  if (failure.status === 401) return 'Session expired. Please sign in again.';
  if (failure.status === 403) {
    return 'Access changed. Sign in with an allowed account or contact support.';
  }
  if (failure.kind === 'network') {
    return 'Usage details are unavailable while Design Work is offline. Check your connection and try again.';
  }
  return 'Usage details are temporarily unavailable. Refresh or try again.';
}

const usageLoadErrorStyle: CSSProperties = {
  margin: '10px 0 0',
  border: '1px solid rgba(185, 28, 28, 0.22)',
  borderRadius: 8,
  background: 'rgba(254, 242, 242, 0.76)',
  color: '#991b1b',
  padding: '10px 12px',
};
